import { useState } from 'react'
import QueryState from '../query_state'
import { Alert, Button, Dialog, FileUpload } from '@cinesend/ui-kit'
import { CheckCircle, Trash } from '@phosphor-icons/react'
import { useQueryClient, useMutation } from '@tanstack/react-query'
import ImportReport from './import_report'
import useStore from '../../hooks/use_store'
import axios from 'axios'

const ImportForm = ({ onClose, cycleID, assetType, importData, queryKey }) => {
  const queryClient = useQueryClient()
  const [uploadErrors, setUploadErrors] = useState(null)

  const { updateModel } = useStore()
  const confirmOrDiscard = (status) => {
    updateModel.mutate(
      {
        endpoint: `api/imports`,
        id: importData.id,
        data: { status: status },
      },
      {
        onSuccess: (res) => {
          if (res.data.data.status === 'discarded') {
            queryClient.invalidateQueries(queryKey)
            return
          }
          // The request doesn't update the status, the job does. So we'll update it here and poll
          queryClient.setQueryData(queryKey, (oldData) => {
            return { ...oldData, import: { ...oldData.import, status: 'confirmation_in_progress' } }
          })
        },
        onError: () => {
          queryClient.invalidateQueries(queryKey)
        },
      }
    )
  }

  const importMutation = useMutation({
    mutationFn: async (file) => {
      setUploadErrors(null)
      const formData = new FormData()
      formData.append('file', file)
      formData.append('asset_type', assetType)
      formData.append('cycle_id', cycleID)

      const response = await axios.post(`/api/imports`, formData, {
        headers: {
          accept: 'application/json',
          Authorization: `Bearer ${localStorage.getItem('user_token')}`,
        },
      })

      return response.data
    },
    onError: (data) => {
      setUploadErrors(data.response?.data?.errors ?? data.response?.data?.message ?? 'Import failed.')
    },
    onSuccess: (data) => {
      setUploadErrors(null)
      queryClient.setQueryData(queryKey, (oldData) => {
        return { ...oldData, import: data.import }
      })
    },
  })

  // An import stuck as pending needs a bailout, so Discard stays available while Confirm is disabled
  const hasPendingImport =
    importData?.status === 'awaiting_confirmation' ||
    importData?.status === 'pending' ||
    importData?.status === 'confirmation_in_progress'
  const isImportRunning =
    importMutation.isLoading || importData?.status === 'pending' || importData?.status === 'confirmation_in_progress'

  return (
    <Dialog
      open
      title='Upload import file'
      size='md'
      disablePointerDismissal={isImportRunning}
      hasCloseButton={!isImportRunning}
      onOpenChange={(open, eventDetails) => {
        if (open) return
        if (isImportRunning) {
          eventDetails?.cancel?.()
          return
        }
        onClose()
      }}
      hasActions={hasPendingImport}
      actionSecondary={
        <Button
          variant='destructive'
          leadingIcon={<Trash size={20} />}
          loading={updateModel.isLoading}
          onClick={() => confirmOrDiscard('discarded')}
        >
          Discard
        </Button>
      }
      actionPrimary={
        <Button
          leadingIcon={<CheckCircle size={20} />}
          loading={updateModel.isLoading}
          disabled={importData?.status === 'pending' || importData?.status === 'confirmation_in_progress'}
          onClick={() => confirmOrDiscard('confirmed')}
        >
          Confirm
        </Button>
      }
    >
      <div className='px-6 pb-6 pt-4 text-sm import-form'>
        {importData?.status === 'awaiting_confirmation' ? (
          <ImportReport importData={importData} />
        ) : (
          <QueryState
            isLoading={
              importMutation.isLoading ||
              importData?.status === 'pending' ||
              importData?.status === 'confirmation_in_progress'
            }
          >
            <FileUpload
              className='h-40'
              accept='.csv'
              message='Drop CSV here'
              buttonLabel='Upload'
              onFileSelected={(file) => importMutation.mutate(file)}
            />
          </QueryState>
        )}
        {uploadErrors && (
          <div className='space-y-4 pt-4'>
            {Array.isArray(uploadErrors) ? (
              uploadErrors.map((line, index) => (
                <Alert tone='error' size='sm' key={index}>
                  {line.message}
                </Alert>
              ))
            ) : (
              <Alert tone='error' size='sm'>
                {uploadErrors}
              </Alert>
            )}
          </div>
        )}
      </div>
    </Dialog>
  )
}

export default ImportForm
